// GL16 GPC 배송비 분담금 단가 관리 — 순수 변환. request 비의존이라 단위 테스트가 쉽다(GL15 mapping.ts 선례).
import dayjs from 'dayjs'
import type {
  GpcRateCountryGroup,
  GpcRateCountryGroupCode,
  GpcRateExcelChange,
  GpcRateExcelSummary,
  GpcRateExcelUploadRes,
  GpcRateExcelValidation,
  GpcRateHistoryFilter,
  GpcRateHistoryItem,
  GpcRateMatrixCell,
  GpcRateMatrixFilter,
  GpcRateMatrixRow,
  GpcRatePrice,
  GpcRateSaveReq,
} from './types'
import { GPC_RATE_COUNTRY_GROUP_CODES, GPC_RATE_COUNTRY_GROUP_NAMES, GPC_RATE_OPEN_END_DATE } from './types'

/** 조회영역 노드 id(생성본 filterKey) — 값은 `wiring.rows.ROWS` 의 q.filters 로 온다. */
export const GPC_RATE_FILTER_KEYS = {
  basisDate: 'baseDatePicker',
  countryGroups: 'countrySelect',
  logisticsCategories: 'logiCategorySelect',
} as const

/** 국가 필터 옵션 — 15개 국가 그룹(응답 전이라 표시명은 상수). 데모 옵션(국가 26개)을 대체한다. */
export const GPC_RATE_COUNTRY_GROUP_OPTIONS: SelectOption<string>[] = GPC_RATE_COUNTRY_GROUP_CODES.map((code) => ({
  value: code,
  label: `${GPC_RATE_COUNTRY_GROUP_NAMES[code]} (${code})`,
}))

const isCountryGroupCode = (v: string): v is GpcRateCountryGroupCode =>
  (GPC_RATE_COUNTRY_GROUP_CODES as readonly string[]).includes(v)

/** unknown → 문자열. 문자열·숫자만 값으로 보고 나머지(객체·null)는 빈 문자열. */
export const asText = (v: unknown): string => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '')

/** 멀티셀렉트 값 → 코드 배열. '' 한 칸은 '전체'(생성본 allOption 규약)라 빈 배열로 본다. */
export const pickMultiValues = (v: unknown): string[] =>
  (Array.isArray(v) ? v : []).map((x) => asText(x).trim()).filter((x) => x !== '')

/**
 * 조회영역 값 → 매트릭스 조회 조건. 기준일이 없으면 null(BE 필수).
 */
export const toMatrixFilter = (filters: Record<string, unknown> | undefined): GpcRateMatrixFilter | null => {
  const basisDate = asText(filters?.[GPC_RATE_FILTER_KEYS.basisDate]).trim()
  if (!basisDate) return null
  const countryGroups = pickMultiValues(filters?.[GPC_RATE_FILTER_KEYS.countryGroups]).filter(isCountryGroupCode)
  const logisticsCategoryCodes = pickMultiValues(filters?.[GPC_RATE_FILTER_KEYS.logisticsCategories])
  return {
    basisDate,
    ...(countryGroups.length ? { countryGroups } : {}),
    ...(logisticsCategoryCodes.length ? { logisticsCategoryCodes } : {}),
  }
}

/** 천단위 콤마. 단가는 0 이상 정수라 소수는 버린다. */
export const formatUnitPrice = (n: number | string) => Number(n).toLocaleString('ko-KR', { maximumFractionDigits: 0 })

/** 예약 단가 셀 둘째 줄 — `MM-DD~ 1,800`(데모 plannedJP "10-01~ 1,800" 과 같은 형식). */
export const toPlannedText = (p: GpcRatePrice) => `${dayjs(p.startDate).format('MM-DD')}~ ${formatUnitPrice(p.unitPrice)}`

/**
 * 셀 표시 문자열 — current = 윗줄(현재 단가), planned = 아랫줄(예약 단가, 여러 건이면 첫 건).
 * 미등록(현재 없음 + 예약 없음)은 둘 다 '' — 생성본이 빗금 + '-' 로 그린다.
 * 현재는 없고 예약만 있으면 윗줄 '-' + 아랫줄 예약.
 */
export const toCellText = (cell: GpcRateMatrixCell | undefined): { current: string; planned: string } => {
  if (!cell) return { current: '', planned: '' }
  const first = cell.reservedPrices[0]
  const planned = first ? toPlannedText(first) : ''
  const current = cell.currentPrice ? formatUnitPrice(cell.currentPrice.unitPrice) : planned ? '-' : ''
  return { current, planned }
}

/** 카테고리 뎁스 경로 — `상의 > 티셔츠 > 반팔티`(null 뎁스는 건너뛴다). */
export const toCategoryPath = (item: Pick<GpcRateMatrixRow, 'categoryDepth1' | 'categoryDepth2' | 'categoryDepth3'>) =>
  [item.categoryDepth1, item.categoryDepth2, item.categoryDepth3].filter((d) => !!d && String(d).trim()).join(' > ')

/** 표 행 — 생성본 컬럼 키(logiCode·logiCat·price{국가}·planned{국가}·updatedAt) + 모달용 원본(catPath·cells). */
export interface GpcRateTableRow extends Record<string, unknown> {
  key: string // = logisticsCategoryCode. 생성본이 `{ key: i, ...r }` 로 스프레드해 인덱스 key 를 덮는다
  catPath: string
  logiCode: string
  logiCat: string
  updatedAt: string
  cells: Partial<Record<GpcRateCountryGroupCode, GpcRateMatrixCell>>
}

/**
 * API 행 → 표 행. 단가 축이 물류카테고리라 물류 카테고리 열(logiCat)에 카테고리 경로를 싣는다.
 * catPath 는 같은 값으로 단가 수정 모달이 읽는다.
 */
export const toMatrixRow = (item: GpcRateMatrixRow): GpcRateTableRow => {
  const catPath = toCategoryPath(item)
  const row: GpcRateTableRow = {
    key: item.logisticsCategoryCode,
    catPath,
    logiCode: item.logisticsCategoryCode,
    logiCat: catPath,
    updatedAt: item.lastUpdatedAt ? dayjs(item.lastUpdatedAt).format('YYYY-MM-DD') : '',
    cells: {},
  }
  item.cells.forEach((cell) => {
    const { code } = cell.countryGroup
    const text = toCellText(cell)
    row[`price${code}`] = text.current
    row[`planned${code}`] = text.planned
    row.cells[code] = cell
  })
  return row
}

/** 응답 countryGroups → 생성본 열 옵션(value = 코드, label = 표시명). */
export const toColumnGroupOptions = (groups: GpcRateCountryGroup[] | undefined): SelectOption<string>[] | undefined =>
  groups?.map((g) => ({ value: g.code, label: g.name }))

/** 셀 클릭 컨텍스트 — 단가 수정 모달(카테고리 × 국가 그룹 1조합)이 본다. */
export interface GpcRateSelectedCell {
  logisticsCategoryCode: string
  categoryPath: string
  countryGroup: GpcRateCountryGroup
  cell: GpcRateMatrixCell
}

/** 표 행 + 클릭한 열의 국가 그룹 코드 → 선택 셀. 응답 셀이 없는 열(필터로 좁힌 열 등)은 null. */
export const toSelectedCell = (row: Record<string, unknown>, countryGroupCode: string): GpcRateSelectedCell | null => {
  if (!isCountryGroupCode(countryGroupCode)) return null
  const cells = row.cells as GpcRateTableRow['cells'] | undefined
  const cell = cells?.[countryGroupCode]
  if (!cell) return null
  return {
    logisticsCategoryCode: asText(row.logiCode) || asText(row.key),
    categoryPath: asText(row.catPath),
    countryGroup: cell.countryGroup,
    cell,
  }
}

/**
 * 새 시작일에 걸치는 기존 행(startDate <= 새 시작일 <= endDate)의 id·version — 낙관적 잠금용.
 * 현재 단가와 예약 단가를 모두 본다(예약 사이에 끼워 넣는 저장도 그 예약 행을 마감한다).
 * 걸치는 행이 없으면 빈 객체(둘 다 생략). version 이 null 이면 짝을 맞출 수 없어 둘 다 생략한다(BE: 함께 보내거나 함께 생략).
 */
export const findBasePrice = (cell: GpcRateMatrixCell, startDate: string): Pick<GpcRateSaveReq, 'basePriceId' | 'basePriceVersion'> => {
  const candidates = [cell.currentPrice, ...cell.reservedPrices].filter((p): p is GpcRatePrice => !!p)
  const hit = candidates.find((p) => p.startDate <= startDate && startDate <= p.endDate)
  if (!hit || hit.version == null) return {}
  return { basePriceId: hit.id, basePriceVersion: hit.version }
}

/** 저장 요청 본문 — 선택 셀 + 검증 통과한 입력값. */
export const toSaveRequest = (selected: GpcRateSelectedCell, input: { price: number; startDate: string }): GpcRateSaveReq => ({
  logisticsCategoryCode: selected.logisticsCategoryCode,
  countryGroup: selected.countryGroup.code,
  unitPrice: input.price,
  startDate: input.startDate,
  ...findBasePrice(selected.cell, input.startDate),
})

const toPeriodText = (p: GpcRatePrice) => `${p.startDate} ~ ${p.endDate === GPC_RATE_OPEN_END_DATE ? '현재' : p.endDate}`

/**
 * 단가 수정 모달 참고 정보 — 생성본 폼 버스(wiring.form.gpcRateEditModal)로 덮어쓰는 파생 필드.
 * 예약 단가는 현재 단가 줄에 이어 붙인다(모달 노드 추가 없이 같은 줄에 표시).
 */
export const toEditModalTexts = (selected: GpcRateSelectedCell | null) => {
  if (!selected) return { catText: '', logiText: '', countryText: '', currentText: '' }
  const { cell, countryGroup } = selected
  const current = cell.currentPrice ? `${formatUnitPrice(cell.currentPrice.unitPrice)} 원 / PCS · ${toPeriodText(cell.currentPrice)}` : '미등록'
  const reserved = cell.reservedPrices.map((p) => `예약 ${p.startDate}~ ${formatUnitPrice(p.unitPrice)} 원`).join(' · ')
  return {
    catText: selected.categoryPath,
    logiText: selected.logisticsCategoryCode,
    countryText: `${countryGroup.name} (${countryGroup.code})`,
    currentText: reserved ? `${current} · ${reserved}` : current,
  }
}

// ── 엑셀 다운로드 ─────────────────────────────────────────────

/** Content-Disposition → 파일명(RFC 5987 UTF-8'' 인코딩·따옴표 대응). 없으면 fallback(GL11 parseExcelFileName 과 같은 규칙). */
export const parseExcelFileName = (disposition: string, fallback: string): string => {
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)/i.exec(disposition)
  if (!match) return fallback
  try {
    return decodeURIComponent(match[1])
  } catch {
    return match[1]
  }
}

/** 다운로드 기본 파일명 — BE 파일명과 같은 규칙(`GPC배송비분담금단가_양식_yyyyMMdd.xlsx`). 헤더를 못 읽을 때만 쓴다. */
export const gpcRateExcelFallbackName = (kind: '양식' | '조회결과', basisDate: string) =>
  `GPC배송비분담금단가_${kind}_${basisDate.replace(/-/g, '')}.xlsx`

// ── 엑셀 업로드 검증 ───────────────────────────────────────────

const toCountryText = (g: GpcRateCountryGroup | null | undefined) => (g ? `${g.name} (${g.code})` : '')
const toPriceText = (n: number | null | undefined) => (n == null ? '미등록' : formatUnitPrice(n))

/** 종료일 표시 — 현행(9999-12-31, BE 무기한 표기)은 '-'. */
export const toEndDateText = (endDate: string) => (endDate === GPC_RATE_OPEN_END_DATE ? '-' : endDate)

/** 검증 모달 한 벌 — 생성본 검증 모달(wiring.rows.ROWS2 의 validation)이 그대로 그린다. */
export interface GpcRateValidationView {
  fileText: string // `파일명 · 기준일 2026-09-22`
  changed: number
  unchanged: number
  unregistered: number
  error: number
  changeRows: Record<string, unknown>[] // 행 · 카테고리 코드 · 카테고리 · 국가 · 현재 단가 · 변경 단가 · 적용 시작일 · 종료일
  errorRows: Record<string, unknown>[] // 행 · 카테고리 코드 · 국가 · 입력값 · 오류 사유
}

/** 검증 응답 → 검증 모달. key 는 행 번호 + 국가(셀 단위라 행 번호만으로는 겹친다) + 순번. */
export const toValidationView = (v: GpcRateExcelValidation, fileName: string): GpcRateValidationView => ({
  fileText: [fileName, `기준일 ${v.basisDate}`].filter(Boolean).join(' · '),
  ...v.summary,
  changeRows: v.changes.map((c, i) => ({
    key: `c-${c.rowNo}-${c.countryGroup.code}-${i}`,
    row: String(c.rowNo),
    catCode: c.logisticsCategoryCode,
    catPath: toCategoryPath(c),
    country: toCountryText(c.countryGroup),
    current: toPriceText(c.currentUnitPrice),
    next: formatUnitPrice(c.unitPrice),
    applyStart: c.startDate,
    applyEnd: toEndDateText(c.endDate),
  })),
  errorRows: v.errors.map((e, i) => ({
    key: `e-${e.rowNo}-${e.countryGroup?.code ?? ''}-${i}`,
    row: String(e.rowNo),
    catCode: e.logisticsCategoryCode ?? '',
    country: toCountryText(e.countryGroup),
    value: e.value ?? '',
    reason: e.message,
  })),
})

/**
 * 확정 저장 전 판정 — 오류가 1건이라도 있으면 'ERROR'(PRD No.10, 전량 차단), 바뀔 셀이 없으면 'NO_CHANGE', 저장 가능하면 null.
 * 오류는 BE 가 저장 시점에 다시 검증해 409 로도 막는다 — 여기서는 요청 전에 먼저 막는다.
 */
export const gpcRateUploadSaveBlock = (summary: GpcRateExcelSummary): 'ERROR' | 'NO_CHANGE' | null => {
  if (summary.error > 0) return 'ERROR'
  if (summary.changed <= 0) return 'NO_CHANGE'
  return null
}

/** PRD No.11 경고(저장은 허용) — 저장 뒤에도 미등록으로 남는 조합 수. 0 이면 확인창 없이 저장한다. */
export const gpcRateUnregisteredWarningCount = (summary: GpcRateExcelSummary): number => Math.max(0, summary.unregistered)

/** 0원 확인(저장은 허용) — 0원으로 바뀌는 셀 수. 0 이면 확인창 없이 저장한다. summary.zeroPrice 가 없으면(구 응답) changes 의 0원 건수로 센다. */
export const gpcRateZeroPriceCount = (summary: GpcRateExcelSummary, changes: GpcRateExcelChange[] = []): number =>
  summary.zeroPrice !== undefined ? Math.max(0, summary.zeroPrice) : changes.filter((c) => c.unitPrice === 0).length

/**
 * 확정 저장 결과의 적용 시작일이 하나뿐이면 그 날짜, 행마다 달라 여럿이면 null.
 * startDates 가 없으면(구 응답) 검증 결과 changes 의 시작일을 중복 제거해 단건·복수를 판단한다(changes 도 없으면 startDate 하나로 본다).
 */
export const toUploadSingleStartDate = (
  res: Pick<GpcRateExcelUploadRes, 'startDate' | 'startDates'> | undefined,
  changes: GpcRateExcelChange[] = [],
): string | null => {
  if (!res) return null
  if (res.startDates?.length) return res.startDates.length === 1 ? res.startDates[0] : null
  if (res.startDates === undefined && changes.length) {
    const distinct = Array.from(new Set(changes.map((c) => c.startDate)))
    if (distinct.length) return distinct.length === 1 ? distinct[0] : null
  }
  return res.startDate ?? null
}

// ── 변경 이력 ─────────────────────────────────────────────────

/** 이력 모달 필터 키(생성본 이력 모달의 필터 버스). 조회영역(메인) 필터와 따로 둔다. */
export const GPC_RATE_HISTORY_FILTER_KEYS = {
  countryGroups: 'historyCountrySelect',
  logisticsCategories: 'historyCategorySelect',
} as const

/** 이력 모달 필터 값 → 이력 조회 조건('전체' = 빈 배열 = 미전송). */
export const toHistoryFilter = (filters: Record<string, unknown> | undefined): GpcRateHistoryFilter => {
  const countryGroups = pickMultiValues(filters?.[GPC_RATE_HISTORY_FILTER_KEYS.countryGroups]).filter(isCountryGroupCode)
  const logisticsCategoryCodes = pickMultiValues(filters?.[GPC_RATE_HISTORY_FILTER_KEYS.logisticsCategories])
  return {
    ...(countryGroups.length ? { countryGroups } : {}),
    ...(logisticsCategoryCodes.length ? { logisticsCategoryCodes } : {}),
  }
}

const toDateTimeText = (v: string | null) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '')

/** 이력 응답 → 이력 표 행(변경일시 · 변경자 · 물류 카테고리 코드 · 카테고리 · 국가 · 이전/변경 단가 · 적용 시작일/종료일 · 구분 · 최종 수정자/일시). */
export const toHistoryRow = (item: GpcRateHistoryItem): Record<string, unknown> => ({
  key: String(item.id),
  changedAt: toDateTimeText(item.createdAt),
  changedBy: item.createdBy ?? '',
  logiCode: item.logisticsCategoryCode,
  catPath: toCategoryPath(item),
  country: toCountryText(item.countryGroup),
  before: item.previousUnitPrice == null ? '-' : formatUnitPrice(item.previousUnitPrice),
  after: formatUnitPrice(item.unitPrice),
  applyStart: item.startDate,
  applyEnd: item.endDate, // 현행은 9999-12-31(BE 무기한 표기) 그대로
  kind: item.changeType?.name ?? '',
  updatedBy: item.updatedBy ?? '',
  updatedAt: toDateTimeText(item.updatedAt),
})
